import React, { useState, useEffect } from 'react';
import { adminApiClient, SubscriptionPlan } from '../lib/api';
import { RichTextEditor } from './common/RichTextEditor';
import { DataTable } from './common/DataTable';
import { showDeleteConfirmation } from '../lib/sweetAlert';
import {
  X,
  Edit2,
  Trash2,
  Plus,
  Star
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ColumnDef } from '@tanstack/react-table';

const SubscriptionPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    currency: 'USD',
    duration: 1,
    billingCycle: 'monthly' as 'monthly' | 'yearly',
    features: {
      maxDonations: null as number | null,
      maxPoojas: null as number | null,
      maxPriests: null as number | null,
    },
    isActive: true,
    isPopular: false,
    displayOrder: 0
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await adminApiClient.getSubscriptionPlans();
      if (response.success && response.data) {
        setPlans((response.data.plans || []).map((p: any) => ({ ...p, id: p._id || p.id })));
      } else {
        toast.error('Failed to fetch subscription plans');
      }
    } catch (err: any) {
      toast.error('Error fetching subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: 0,
      currency: 'USD',
      duration: 1,
      billingCycle: 'monthly',
      features: {
        maxDonations: null,
        maxPoojas: null,
        maxPriests: null,
      },
      isActive: true,
      isPopular: false,
      displayOrder: 0
    });
    setEditingPlan(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const toastId = toast.loading('Saving plan...');
    try {
      const features: any = {};

      if (formData.features.maxDonations && formData.features.maxDonations > 0) features.maxDonations = formData.features.maxDonations;
      if (formData.features.maxPoojas && formData.features.maxPoojas > 0) features.maxPoojas = formData.features.maxPoojas;
      if (formData.features.maxPriests && formData.features.maxPriests > 0) features.maxPriests = formData.features.maxPriests;

      const submitData: Partial<SubscriptionPlan> = {
        name: formData.name,
        description: formData.description,
        price: formData.price,
        currency: formData.currency,
        duration: formData.duration,
        billingCycle: formData.billingCycle,
        features,
        isActive: formData.isActive,
        isPopular: formData.isPopular,
        displayOrder: formData.displayOrder
      };

      let response;
      if (editingPlan) {
        response = await adminApiClient.updateSubscriptionPlan(editingPlan._id, submitData);
      } else {
        response = await adminApiClient.createSubscriptionPlan(submitData);
      }

      if (response.success) {
        await fetchPlans();
        setShowForm(false);
        resetForm();
        toast.success('Plan saved successfully', { id: toastId });
      } else {
        toast.error(response.message || 'Failed to save', { id: toastId });
      }
    } catch (err: any) {
      toast.error(err.message || 'Error saving plan', { id: toastId });
    }
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      price: plan.price,
      currency: plan.currency,
      duration: plan.duration,
      billingCycle: plan.billingCycle,
      features: {
        maxDonations: plan.features.maxDonations ?? null,
        maxPoojas: plan.features.maxPoojas ?? null,
        maxPriests: plan.features.maxPriests ?? null,
      },
      isActive: plan.isActive,
      isPopular: plan.isPopular || false,
      displayOrder: plan.displayOrder
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const isConfirmed = await showDeleteConfirmation('Delete Plan?', 'This action cannot be undone.');
    if (!isConfirmed) return;

    try {
      const response = await adminApiClient.deleteSubscriptionPlan(id);
      if (response.success) {
        fetchPlans();
        toast.success('Plan deleted');
      } else {
        toast.error('Failed to delete plan');
      }
    } catch (err) {
      toast.error('Error deleting plan');
    }
  };

  const columns: ColumnDef<SubscriptionPlan>[] = [
    {
      accessorKey: 'name',
      header: 'Plan Name',
      cell: (info) => (
        <div>
          <div className="font-medium text-gray-900 flex items-center gap-2">
            {info.getValue<string>()}
            {info.row.original.isPopular && <Star size={12} className="text-yellow-500 fill-yellow-500" />}
          </div>
          <div className="text-xs text-gray-500 capitalize">{info.row.original.billingCycle}</div>
        </div>
      )
    },
    {
      accessorKey: 'price',
      header: 'Price',
      cell: (info) => (
        <div className="font-bold text-gray-900">
          {info.getValue<number>() === 0 ? 'Free' : `$${info.getValue<number>()}`}
          <span className="text-xs font-normal text-gray-500 ml-1">{info.row.original.currency}</span>
        </div>
      )
    },
    {
      accessorKey: 'duration',
      header: 'Duration',
      cell: (info) => (
        <span className="text-sm text-gray-700">{info.getValue<number>()} Month(s)</span>
      )
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: (info) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${info.getValue<boolean>() ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
          {info.getValue<boolean>() ? 'Active' : 'Inactive'}
        </span>
      )
    },

    {
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className="flex gap-2">
          <button onClick={() => handleEdit(info.row.original)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => {
              const plan = info.row.original as any;
              handleDelete(plan._id || plan.id);
            }}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
            title="Remove Plan"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription Plans</h1>
          <p className="text-sm text-gray-500">Manage pricing tiers and features</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm transition-colors">
          <Plus size={20} /> Add Plan
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
              <h3 className="font-bold text-lg text-gray-900">{editingPlan ? 'Edit Plan' : 'Create New Plan'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <form id="planForm" onSubmit={handleSubmit} className="space-y-8">
                {/* Basic Info */}
                <section className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider border-b pb-2">Basic Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name *</label>
                      <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 outline-none" placeholder="e.g. Pro Plan" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                      <input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} value={formData.displayOrder} onChange={e => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500/20 outline-none" />
                    </div>
                  </div>
                  <div>
                    <RichTextEditor label="Description" value={formData.description} onChange={val => setFormData({ ...formData, description: val })} placeholder="Plan description..." />
                  </div>
                </section>

                {/* Pricing */}
                <section className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider border-b pb-2">Pricing & Cycle</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2 md:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                      <input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} step="0.01" min="0" required value={formData.price} onChange={e => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg outline-none" />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                      <select value={formData.currency} onChange={e => setFormData({ ...formData, currency: e.target.value })} className="w-full px-3 py-2 border rounded-lg outline-none">
                        <option value="USD">USD</option>
                        <option value="INR">INR</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Duration (Months)</label>
                      <input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} min="1" required value={formData.duration} onChange={e => setFormData({ ...formData, duration: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 border rounded-lg outline-none" />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cycle</label>
                      <select value={formData.billingCycle} onChange={e => setFormData({ ...formData, billingCycle: e.target.value as any })} className="w-full px-3 py-2 border rounded-lg outline-none">
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  </div>
                </section>

                {/* Features */}
                <section className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider border-b pb-2">Limits & Features</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><label className="text-sm text-gray-700">Max Donations</label><input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} placeholder="Unlimited" value={formData.features.maxDonations || ''} onChange={e => setFormData({ ...formData, features: { ...formData.features, maxDonations: parseInt(e.target.value) || null } })} className="w-full px-3 py-2 border rounded-lg outline-none mt-1" /></div>
                    <div><label className="text-sm text-gray-700">Max Poojas</label><input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} placeholder="Unlimited" value={formData.features.maxPoojas || ''} onChange={e => setFormData({ ...formData, features: { ...formData.features, maxPoojas: parseInt(e.target.value) || null } })} className="w-full px-3 py-2 border rounded-lg outline-none mt-1" /></div>
                    <div><label className="text-sm text-gray-700">Max Priests</label><input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} placeholder="Unlimited" value={formData.features.maxPriests || ''} onChange={e => setFormData({ ...formData, features: { ...formData.features, maxPriests: parseInt(e.target.value) || null } })} className="w-full px-3 py-2 border rounded-lg outline-none mt-1" /></div>
                  </div>

                </section>



                <section className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} className="rounded text-green-600 outline-none" />
                    <span className="font-medium text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.isPopular} onChange={e => setFormData({ ...formData, isPopular: e.target.checked })} className="rounded text-yellow-500 outline-none" />
                    <span className="font-medium text-gray-700">Popular</span>
                  </label>
                </section>
              </form>
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" form="planForm" disabled={loading} className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm">{loading ? 'Saving...' : 'Save Plan'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          data={plans}
          columns={columns}
          loading={loading}
          enableClientSidePagination={true}
        />
      </div>
    </div>
  );
};

export default SubscriptionPlansPage;
